import React, { useRef, useState } from 'react';
import { FileData } from '../types';

interface CopilotFileUploadProps {
  title: string;
  subtitle: string;
  files: FileData[];
  multiple?: boolean;
  onUpdate: (files: FileData[]) => void;
}

const CopilotFileUpload: React.FC<CopilotFileUploadProps> = ({
  title,
  subtitle,
  files,
  multiple = false,
  onUpdate,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const processFiles = (fileList: FileList) => {
    const newFiles: FileData[] = [];
    let remaining = fileList.length;

    Array.from(fileList).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          newFiles.push({
            file,
            base64: event.target.result as string,
            mimeType: file.type,
          });
        }
        remaining -= 1;
        if (remaining === 0) {
          onUpdate(multiple ? [...files, ...newFiles] : [newFiles[0]]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFile = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    const updated = [...files];
    updated.splice(index, 1);
    onUpdate(updated);
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="application/pdf,image/*"
        multiple={multiple}
        onChange={(event) => {
          if (event.target.files?.length) {
            processFiles(event.target.files);
          }
          if (event.target.value) event.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          if (event.dataTransfer.files.length) {
            processFiles(event.dataTransfer.files);
          }
        }}
        className={`w-full rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-all ${
          files.length
            ? 'border-accent bg-accent/5'
            : isDragging
              ? 'border-accent bg-accent/10'
              : 'border-stone-300 bg-white hover:border-accent/60 hover:bg-accent/5'
        }`}
      >
        {files.length > 0 ? (
          <div className="space-y-3">
            {files.map((fileData, index) => (
              <div
                key={`${fileData.file.name}-${index}`}
                className="flex items-center justify-between rounded-xl border border-accent/20 bg-white px-4 py-3 text-left"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-stone-900">{fileData.file.name}</p>
                  <p className="text-xs text-stone-500">{multiple ? 'Answer sheet uploaded' : 'Ready to use'}</p>
                </div>
                <button
                  type="button"
                  onClick={(event) => removeFile(event, index)}
                  className="ml-3 text-stone-400 hover:text-red-600"
                  aria-label="Remove file"
                >
                  ×
                </button>
              </div>
            ))}
            {!multiple && <p className="text-xs text-accent">Click to replace</p>}
            {multiple && (
              <p className="text-xs font-medium text-accent">Add more answer sheets</p>
            )}
          </div>
        ) : (
          <>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center text-accent">
              <svg className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
            <p className="text-base font-semibold text-stone-900">{title}</p>
            <p className="mt-1 text-sm text-stone-500">{subtitle}</p>
          </>
        )}
      </button>
    </div>
  );
};

export default CopilotFileUpload;
